'use client';

import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Gifting host context — the host (the Morfi web app) supplies the
 * concrete chain-submit hook implementations and the SDK gift surface
 * (`useSendUsdcGift`, `useSendOutcomeTokenGift`, `useClaimGift`,
 * `useWithdrawGift`) delegates to them.
 *
 * Unlike the trading host context — which OWNS the buy/sell/swap/boost
 * orchestration in the SDK package and only takes bindings (`submitTx`,
 * `getConnection`, `turnkeyDeps`) from the host — gifting takes the full
 * hook implementation from the host. Rationale: the gift transaction
 * builders (`buildGiftUsdcSend`, `buildClaimVersionedTx`, etc.) live at
 * `frontend/src/lib/solana/` today, and an in-place cross-package move
 * into `packages/contract-sdk/src/gifts/` is a deferral
 * ([`DEFERRED.md`](../../../../DEFERRED.md) DEF-12 — to be filed).
 *
 * The SDK surface still owns:
 *   - the public hook name (`useSendUsdcGift`, etc.)
 *   - the type contract for params + return shape
 *   - the manifest registration (so SDK consumers discover the hook via
 *     the public barrel + IDE autocomplete works)
 *
 * The host owns:
 *   - the actual mutation function body (signs the tx, submits it,
 *     handles toasts, fires analytics events, runs the post-confirm
 *     permalink poll)
 *
 * Pattern: `<GiftingHostProvider value={{ useSendUsdcGift, … }}>` mounts
 * once at the app root; each SDK gift hook reads the corresponding
 * implementation from context and invokes it.
 */
import { createContext, useContext } from 'react';
const GiftingHostContext = createContext(null);
/**
 * Mounts the gifting host bindings. Place INSIDE `<SDKProvider>` and
 * outside any gift-consuming component. The Morfi app's
 * `sdk-v2-provider.tsx` mounts this.
 */
export function GiftingHostProvider({
  value,
  children
}) {
  return _jsx(GiftingHostContext.Provider, {
    value: value,
    children: children
  });
}
/**
 * Read the gifting host bindings inside an SDK gift hook. Throws if
 * the host didn't mount `<GiftingHostProvider>`.
 */
export function useGiftingHostContext() {
  const ctx = useContext(GiftingHostContext);
  if (ctx === null) {
    throw new Error("[backend-sdk-v2] useGiftingHostContext: missing <GiftingHostProvider>. Wrap the app in <GiftingHostProvider value={\u2026}> before calling any SDK gift hook.");
  }
  return ctx;
}